import { useMidi } from '@tunasong/audio-ui'
import type { MidiNoteEvent } from '@tunasong/audio-ui'
import { MelodyLib } from '@tunasong/music-lib'
import { useRef, useState } from 'react'

/**
 * Live MIDI into raw notes.
 *
 * The hook does one thing: it timestamps events as they arrive and hands the list to the pure
 * pairing function. Every decision about what a retrigger means, what an orphan noteoff means and
 * what happens to a note still held at the end of the take lives in `midiEventsToRawNotes`, where it
 * is testable without a device - and a device is the one thing this worktree cannot have.
 *
 * `performance.now()` and not the event's own `timestamp`: the take runner's `t0` is on the same
 * clock, and webmidi's timestamps are relative to the MIDIAccess object's own time origin.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

/** A device that reports no attack is treated as mezzo-forte rather than silent. */
const DEFAULT_ATTACK = 0.8

export const toRawMidiEvent = (event: MidiNoteEvent, timeMs: number): MelodyLib.RawMidiEvent => ({
  type: event.type,
  midi: event.note.number,
  velocity: event.type === 'noteon' ? (event.note.attack ?? DEFAULT_ATTACK) : 0,
  timeMs,
})

export interface MidiCapture {
  /** Clear the buffer. Called when a take starts, so a previous take's notes cannot leak in. */
  arm: () => void
  /** Pair everything collected. `endMs` closes notes the player is still holding. */
  collect: (t0Ms: number, endMs: number) => MelodyLib.RawNote[]
  /** Whether any MIDI input is connected - the modality picker disables MIDI without one. */
  hasInput: boolean
}

export const useMidiCapture = (): MidiCapture => {
  const events = useRef<MelodyLib.RawMidiEvent[]>([])
  /**
   * A ref beside the state: the `onNoteEvent` callback is registered once per `inputs` change and
   * would otherwise close over a stale `armed`, so a take's first notes would be dropped.
   */
  const armed = useRef(false)
  const [, setArmedFlag] = useState(false)

  const { inputs } = useMidi({
    onNoteEvent: event => {
      if (!armed.current) {
        return
      }
      events.current.push(toRawMidiEvent(event, performance.now()))
    },
  })

  return {
    arm: () => {
      events.current = []
      armed.current = true
      setArmedFlag(true)
    },
    collect: (t0Ms, endMs) => {
      armed.current = false
      setArmedFlag(false)
      return MelodyLib.midiEventsToRawNotes(events.current, t0Ms, endMs)
    },
    hasInput: inputs.length > 0,
  }
}
